import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Icon } from '@iconify/react';
import { supabase, sanitizeInput, sanitizeHtml } from '../../lib/supabase';
import { ARTICLE_CATEGORIES, type ArticleCategory } from '../../types';
import { RichTextEditor } from '../../components/RichTextEditor';

interface Article {
  title: string;
  slug: string;
  content: string;
  excerpt: string;
  tags: string[];
  published: boolean;
  reading_time: number;
  banner_image: string;
  publish_date: string | null;
  category: ArticleCategory;
  // SEO fields
  meta_title: string;
  meta_description: string;
  meta_keywords: string[];
  featured_image: string;
}

export function ArticleEditor() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [article, setArticle] = useState<Article>({
    title: '',
    slug: '',
    content: '',
    excerpt: '',
    tags: [],
    published: false,
    reading_time: 5,
    banner_image: '',
    publish_date: null,
    category: 'Technology',
    meta_title: '',
    meta_description: '',
    meta_keywords: [],
    featured_image: ''
  });
  const [schedulePublish, setSchedulePublish] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [seoKeywordsInput, setSeoKeywordsInput] = useState('');

  useEffect(() => {
    if (id) {
      fetchArticle();
    }
  }, [id]);

  // Auto-generate slug from title
  useEffect(() => {
    if (article.title && !article.slug) {
      const autoSlug = article.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, '');
      setArticle(prev => ({ ...prev, slug: autoSlug }));
    }
  }, [article.title, article.slug]);

  const fetchArticle = async () => {
    if (!id) return;
    
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('articles')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;

      if (data) {
        setArticle({
          title: data.title,
          slug: data.slug || '',
          content: data.content || '',
          excerpt: data.excerpt || '',
          tags: data.tags || [],
          published: data.published || false,
          reading_time: data.reading_time || 5,
          banner_image: data.banner_image || '',
          publish_date: data.publish_date,
          category: (data.category as ArticleCategory) || 'Technology',
          meta_title: data.meta_title || '',
          meta_description: data.meta_description || '',
          meta_keywords: data.meta_keywords || [],
          featured_image: data.featured_image || ''
        });
        setTagInput(data.tags?.join(', ') || '');
        setSeoKeywordsInput(data.meta_keywords?.join(', ') || '');
        setSchedulePublish(!!data.publish_date);
      }
    } catch (error) {
      console.error('Error fetching article:', error);
      setError('Failed to load article');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const articleData = {
        title: sanitizeInput(article.title),
        slug: article.slug || (article.title ? article.title.toLowerCase().replace(/[^a-z0-9\s]+/g, '').replace(/\s+/g, '-').replace(/(^-|-$)+/g, '') : ''),
        content: sanitizeHtml(article.content),
        excerpt: sanitizeInput(article.excerpt),
        tags: article.tags.map(tag => sanitizeInput(tag)),
        published: article.published,
        reading_time: article.reading_time,
        banner_image: article.banner_image,
        publish_date: schedulePublish ? article.publish_date : null,
        category: article.category,
        meta_title: article.meta_title || article.title,
        meta_description: article.meta_description || article.excerpt,
        meta_keywords: article.meta_keywords,
        featured_image: article.featured_image || article.banner_image,
        author_id: id ? undefined : (await supabase.auth.getUser()).data.user?.id,
        updated_at: new Date().toISOString()
      };

      const { error } = id
        ? await supabase
            .from('articles')
            .update(articleData)
            .eq('id', id)
        : await supabase
            .from('articles')
            .insert([articleData]);

      if (error) throw error;
      navigate('/admin/articles');
    } catch (error) {
      console.error('Error saving article:', error);
      setError('Failed to save article');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTagsChange = (value: string) => {
    setTagInput(value);
    // Split by commas, trim whitespace, and filter out empty strings
    const newTags = value
      .split(',')
      .map(tag => tag.trim())
      .filter(Boolean);
    setArticle(prev => ({ ...prev, tags: newTags }));
  };

  const handleSeoKeywordsChange = (value: string) => {
    setSeoKeywordsInput(value);
    // Split by commas, trim whitespace, and filter out empty strings
    const newKeywords = value
      .split(',')
      .map(keyword => keyword.trim())
      .filter(Boolean);
    setArticle(prev => ({ ...prev, meta_keywords: newKeywords }));
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsLoading(true);
      setError(null);

      // Enhanced security validations
      if (file.size > 5 * 1024 * 1024) {
        throw new Error('Image must be less than 5MB');
      }

      // Validate file type more strictly
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
      if (!allowedTypes.includes(file.type)) {
        throw new Error('Only JPEG, PNG, WebP, and GIF images are allowed');
      }

      // Validate file extension
      const fileExt = file.name.split('.').pop()?.toLowerCase();
      const allowedExtensions = ['jpg', 'jpeg', 'png', 'webp', 'gif'];
      if (!fileExt || !allowedExtensions.includes(fileExt)) {
        throw new Error('Invalid file extension');
      }

      // Use UUID for better security
      const fileName = `${crypto.randomUUID()}.${fileExt}`;
      const filePath = `${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('article-images')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('article-images')
        .getPublicUrl(filePath);

      setArticle(prev => ({ ...prev, banner_image: publicUrl }));
    } catch (error) {
      console.error('Error uploading image:', error);
      setError(error instanceof Error ? error.message : 'Error uploading image');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Icon icon="mdi:loading" className="animate-spin h-8 w-8 text-primary-500" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-8 pt-6 pb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {id ? 'Edit Article' : 'New Article'}
          </h1>
          <div className="flex items-center gap-3 flex-shrink-0">
            <button
              onClick={() => navigate('/admin/articles')}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-all duration-200"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Cancel
            </button>
            
            <button
              onClick={handleSubmit}
              disabled={isLoading}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg hover:from-orange-600 hover:to-orange-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              {isLoading ? 'Saving...' : 'Save Changes'}
            </button>

            {/* Save as Draft */}
            <button
              type="button"
              onClick={async () => {
                setArticle(prev => ({ ...prev, published: false }));
                setTimeout(async () => {
                  const event = new Event('submit', { bubbles: true, cancelable: true });
                  await handleSubmit(event as any);
                }, 100);
              }}
              disabled={isLoading}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Save as Draft
            </button>
            
            {/* Publish Now */}
            <button
              type="button"
              onClick={async () => {
                setArticle(prev => ({ ...prev, published: true }));
                setSchedulePublish(false);
                setTimeout(async () => {
                  const event = new Event('submit', { bubbles: true, cancelable: true });
                  await handleSubmit(event as any);
                }, 100);
              }}
              disabled={isLoading}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
              Publish Now
            </button>

            {/* Schedule Publication */}
            {article.publish_date && (
              <button
                type="button"
                onClick={async () => {
                  setArticle(prev => ({ ...prev, published: true }));
                  setTimeout(async () => {
                    const event = new Event('submit', { bubbles: true, cancelable: true });
                    await handleSubmit(event as any);
                  }, 100);
                }}
                disabled={isLoading}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
                title={`Schedule for: ${new Date(article.publish_date).toLocaleString()}`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Schedule
              </button>
            )}
          </div>
        </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6"
      >
        {error && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Banner Image
            </label>
            <div className="flex items-center gap-4">
              {article.banner_image && (
                <div className="relative w-40 h-24 rounded-lg overflow-hidden">
                  <img
                    src={article.banner_image}
                    alt="Banner"
                    className="w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => setArticle(prev => ({ ...prev, banner_image: '' }))}
                    className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                  >
                    <Icon icon="lucide:x" width="16" height="16" />
                  </button>
                </div>
              )}
              <div className="flex-1">
                <label
                  className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:border-primary-500 transition-colors"
                >
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <Icon icon="lucide:upload" className="w-8 h-8 text-gray-400" />
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                      Click to upload or drag and drop
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      PNG, JPG up to 5MB
                    </p>
                  </div>
                  <input
                    type="file"
                    className="hidden"
                    accept="image/*"
                    onChange={handleImageUpload}
                  />
                </label>
              </div>
            </div>
          </div>

          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Title
            </label>
            <input
              type="text"
              id="title"
              value={article.title}
              onChange={(e) => setArticle({ ...article, title: e.target.value })}
              className="mt-1 block w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 ease-in-out text-base leading-tight shadow-sm hover:border-gray-400 dark:hover:border-gray-500"
              placeholder="Enter article title..."
              required
            />
          </div>

          <div>
            <label htmlFor="slug" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Article Slug
            </label>
            <input
              type="text"
              id="slug"
              value={article.slug || (article.title ? article.title.toLowerCase().replace(/[^a-z0-9\s]+/g, '').replace(/\s+/g, '-').replace(/(^-|-$)+/g, '') : '')}
              onChange={(e) => {
                const slug = e.target.value.toLowerCase().replace(/[^a-z0-9\s]+/g, '').replace(/\s+/g, '-').replace(/(^-|-$)+/g, '');
                setArticle({ ...article, slug });
              }}
              className="mt-1 block w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 ease-in-out text-base leading-tight shadow-sm hover:border-gray-400 dark:hover:border-gray-500"
              placeholder="article-slug-url"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              URL-friendly version of your title (auto-generated from title)
            </p>
          </div>

          <div className="relative">
            <label htmlFor="category" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Category
            </label>
            <div className="relative">
               <select
                 id="category"
                 value={article.category}
                 onChange={(e) => setArticle({ ...article, category: e.target.value as ArticleCategory })}
                 className="mt-1 block w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 ease-in-out text-base leading-tight shadow-sm hover:border-gray-400 dark:hover:border-gray-500 appearance-none cursor-pointer"
               >
                 {Object.entries(ARTICLE_CATEGORIES).map(([category, emoji]) => (
                   <option key={category} value={category} className="py-1 text-sm">
                     {emoji} {category}
                   </option>
                 ))}
               </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <svg className="w-5 h-5 text-gray-400 dark:text-gray-500 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>

          <div>
            <label htmlFor="content" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Content
            </label>
            <RichTextEditor
              content={article.content}
              onChange={(content) => setArticle({ ...article, content })}
              placeholder="Write your article content here..."
            />
          </div>

          <div>
            <label htmlFor="excerpt" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Excerpt
            </label>
            <RichTextEditor
              content={article.excerpt}
              onChange={(excerpt) => setArticle({ ...article, excerpt })}
              placeholder="Write a brief summary of your article..."
            />
          </div>

          <div>
            <label htmlFor="tags" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Tags
            </label>
            <input
              type="text"
              id="tags"
              value={tagInput}
              onChange={(e) => handleTagsChange(e.target.value)}
              className="mt-1 block w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 ease-in-out text-base leading-tight shadow-sm hover:border-gray-400 dark:hover:border-gray-500"
              placeholder="React, Architecture, TypeScript..."
            />
            {article.tags.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {article.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-sm"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div>
            <label htmlFor="readingTime" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Reading Time
            </label>
            <input
              type="text"
              id="readingTime"
              value={article.reading_time}
              onChange={(e) => setArticle({ ...article, reading_time: parseInt(e.target.value) || 5 })}
              className="mt-1 block w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 ease-in-out text-base leading-tight shadow-sm hover:border-gray-400 dark:hover:border-gray-500"
              placeholder="5 min read"
            />
          </div>

          {/* SEO Section */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              SEO & Social Media
            </h3>
            
            <div className="space-y-6">
              <div>
                <label htmlFor="seoTitle" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  SEO Title
                </label>
                <input
                  type="text"
                  id="seoTitle"
                  value={article.meta_title}
                  onChange={(e) => setArticle({ ...article, meta_title: e.target.value })}
                  className="mt-1 block w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 ease-in-out text-base leading-tight shadow-sm hover:border-gray-400 dark:hover:border-gray-500"
                  placeholder="Leave empty to use article title"
                  maxLength={60}
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {article.meta_title.length}/60 characters (optimal: 50-60)
                </p>
              </div>

              <div>
                <label htmlFor="seoDescription" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  SEO Description
                </label>
                <textarea
                  id="seoDescription"
                  value={article.meta_description}
                  onChange={(e) => setArticle({ ...article, meta_description: e.target.value })}
                  rows={3}
                  className="mt-1 block w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 ease-in-out text-base leading-relaxed shadow-sm hover:border-gray-400 dark:hover:border-gray-500"
                  placeholder="Leave empty to use article excerpt"
                  maxLength={160}
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {article.meta_description.length}/160 characters (optimal: 150-160)
                </p>
              </div>

              <div>
                <label htmlFor="seoKeywords" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  SEO Keywords
                </label>
                <input
                  type="text"
                  id="seoKeywords"
                  value={seoKeywordsInput}
                  onChange={(e) => handleSeoKeywordsChange(e.target.value)}
                  className="mt-1 block w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 ease-in-out text-base leading-tight shadow-sm hover:border-gray-400 dark:hover:border-gray-500"
                  placeholder="web development, react, typescript..."
                />
                {article.meta_keywords.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {article.meta_keywords.map((keyword, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 rounded-full text-sm"
                      >
                        {keyword}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label htmlFor="ogImage" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Social Media Image URL
                </label>
                <input
                  type="url"
                  id="ogImage"
                  value={article.featured_image}
                  onChange={(e) => setArticle({ ...article, featured_image: e.target.value })}
                  className="mt-1 block w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 ease-in-out text-base leading-tight shadow-sm hover:border-gray-400 dark:hover:border-gray-500"
                  placeholder="Leave empty to use banner image"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Used for social media sharing (Twitter, Facebook, etc.)
                </p>
              </div>

              {/* Schedule Publication */}
              <div>
                <label htmlFor="publishDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Schedule Publication (Optional)
                </label>
                <input
                  type="datetime-local"
                  id="publishDate"
                  value={article.publish_date || ''}
                  onChange={(e) => {
                    setArticle({ ...article, publish_date: e.target.value });
                    setSchedulePublish(!!e.target.value);
                  }}
                  min={new Date().toISOString().slice(0, 16)}
                  className="mt-1 block w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 ease-in-out text-base leading-tight shadow-sm hover:border-gray-400 dark:hover:border-gray-500"
                  placeholder="Select date and time to schedule publication"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Leave empty to publish immediately, or set a future date to schedule publication
                </p>
              </div>
            </div>
          </div>


        </form>
      </motion.div>
    </div>
  );
}